import styled from '@emotion/styled';
import { alpha } from '@mui/material/styles';

import { focusRing, hoverable } from '@/shared/design-system/interaction-states';

export const CategoryList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

export const CategoryCard = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.colors.background.canvas,
  borderRadius: 10,
  overflow: 'hidden',
}));

export const CardHeader = styled('button')(({ theme }) => ({
  ...hoverable(theme),
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  minHeight: 48,
  padding: '12px',
  boxSizing: 'border-box',
  border: 'none',
  borderRadius: 0,
  backgroundColor: theme.colors.border.default,
  color: theme.colors.text.primary,
  cursor: 'pointer',
  textAlign: 'left',
}));

export const CardDot = styled('span')<{ $color: string }>(({ $color }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: $color,
  flexShrink: 0,
}));

export const CardBadge = styled('span')<{ $color: string }>(({ $color }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 20,
  borderRadius: 6,
  backgroundColor: alpha($color, 0.13),
  color: $color,
  fontSize: 12,
  fontWeight: 600,
  padding: '0 6px',
  boxSizing: 'border-box',
  flexShrink: 0,
}));

export const ChevronBox = styled('span', {
  shouldForwardProp: (prop) => prop !== '$open',
})<{ $open: boolean }>(({ $open }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  marginLeft: 'auto',
  flexShrink: 0,
  transform: `rotate(${$open ? 180 : 0}deg)`,
  transition: 'transform 0.15s ease',

  '& svg': { width: '100%', height: '100%' },
}));

export const RowsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: '8px 0',
});

export const LeafItem = styled('div', {
  shouldForwardProp: (prop) => !['$selected', '$color', '$depth'].includes(prop as string),
})<{ $selected: boolean; $color: string; $depth: number }>(
  ({ theme, $selected, $color, $depth }) => ({
    ...hoverable(theme, 'surface', '&:not(:has(button:hover))'),
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: `8px 16px 8px ${16 + $depth * 16}px`,
    cursor: 'pointer',
    backgroundColor: $selected ? alpha($color, 0.15) : 'transparent',
    transition: 'background-color 0.15s',
    boxSizing: 'border-box',
    minWidth: 0,

    '&:hover': {
      backgroundColor: $selected ? alpha($color, 0.22) : theme.colors.white[4],
    },
  }),
);

export const RadioSelection = styled('div')(({ theme }) => ({
  ...hoverable(theme),
  ...focusRing(theme),
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 1,
  minWidth: 0,
  cursor: 'pointer',

  '& [role="radio"]': { flexShrink: 0 },
  '& span:last-of-type': { minWidth: 0, overflowWrap: 'anywhere' },
  '&:has(button:hover), &:has([role="radio"]:hover)': {
    transition: 'none',
  },
}));

export const DisclosureButton = styled('button')(({ theme }) => ({
  ...hoverable(theme),
  ...focusRing(theme),
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: 0,
  border: 0,
  background: 'transparent',
  color: theme.colors.text.primary,
  cursor: 'pointer',
  flexShrink: 0,
}));

export const EmptyRows = styled('div')(({ theme }) => ({
  padding: '8px 16px',
  color: theme.colors.text.tertiary,
}));
